import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ReviewVote } from '@api';

import type { ReviewContentReviewData } from '@shared/api/types/review';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { useVoteForReviewMutation } from '@query/app';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

const useReviewFeedbackVote = ({ review }: { review: ReviewContentReviewData }) => {
  const initialData = review.userVote || 0;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = ['reviewVote', user?.id, review.id, review.userVote];
  const { data: oldValue } = useQuery<ReviewVote>({
    queryKey,
    staleTime: Infinity,
    queryFn: () => initialData,
    initialData,
  });
  const [nextValue, setNextValue] = useState<ReviewVote | null>(null);
  const value = nextValue === null ? oldValue : nextValue;

  const { mutateAsync, isPending } = useVoteForReviewMutation({
    onMutate: ({ vote }) => {
      const previousData = queryClient.getQueryData<ReviewVote>(queryKey);

      queryClient.setQueryData(queryKey, vote);

      return previousData;
    },
    onError: (error, variables, vote) => {
      queryClient.setQueryData(queryKey, vote);
    },
  });

  useEffect(() => {
    if (isPending || oldValue === value) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        await mutateAsync({
          reviewId: review.id,
          reviewType: review.type,
          vote: value,
        });
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setNextValue(null);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPending, value, mutateAsync, oldValue, review.id, review.type]);

  return [value, setNextValue] as const;
};

export default useReviewFeedbackVote;
